import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ExternalLink, Loader2, Wallet } from "lucide-react";
import { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import type { ApiSchemas } from "@/shared/api/schema";
import { ROUTES } from "@/shared/model/routes";
import { InfoBlock } from "@/shared/ui/info-block";
import { InfoTooltip } from "@/shared/ui/info-tooltip";
import { Button } from "@/shared/ui/kit/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/kit/form";
import { Input } from "@/shared/ui/kit/input";
import { Price } from "@/shared/ui/price";
import { ResponsiveModal } from "@/shared/ui/responsive-modal";

import { useCreateDeposit } from "../model/use-create-deposit";
import { DepositCountdown } from "../ui/deposit-countdown";

const QUICK_AMOUNTS = [10, 25, 50, 100, 250, 500] as const;
const DEFAULT_AMOUNT = 50;

const AMOUNT_CONSTRAINTS = {
  min: 1,
  max: 999999,
} as const;

interface CreateDepositDialogProps {
  isOpen: boolean;
  onOpenChange: (value: boolean) => void;
  modal?: boolean;
}

export function CreateDepositDialogCompose({
  isOpen,
  onOpenChange,
  modal = true,
}: CreateDepositDialogProps) {
  const [depositData, setDepositData] = useState<
    ApiSchemas["DepositRequest"] | null
  >(null);
  const createDeposit = useCreateDeposit();

  const formSchema = useMemo(
    () =>
      z.object({
        amount: z
          .number({ message: "Введите сумму" })
          .min(AMOUNT_CONSTRAINTS.min, `Минимум ${AMOUNT_CONSTRAINTS.min} USD`)
          .max(
            AMOUNT_CONSTRAINTS.max,
            `Максимум ${AMOUNT_CONSTRAINTS.max} USD`,
          ),
      }),
    [],
  );

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: DEFAULT_AMOUNT,
    },
  });

  const amount = form.watch("amount");

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      form.reset({ amount: DEFAULT_AMOUNT });
      setDepositData(null);
    }, 300);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    if (raw === "") {
      form.setValue("amount", 0, { shouldValidate: true });
      return;
    }
    const val = Number.parseInt(raw, 10);
    if (!Number.isNaN(val) && val <= AMOUNT_CONSTRAINTS.max) {
      form.setValue("amount", val, { shouldValidate: true });
    }
  };

  const handleOpenPayment = () => {
    if (depositData?.heleket_url) {
      window.open(depositData.heleket_url, "_blank");
    }
  };

  const handleCreate = form.handleSubmit(async (data) => {
    try {
      const result = await createDeposit.mutateAsync({
        body: {
          deposit_request: { amount_cents: data.amount * 100 },
        },
      });
      if (result) {
        setDepositData(result);
      }
    } catch (error) {
      const apiError = error as { error?: { code?: string; message?: string } };
      if (apiError?.error?.code === "deposit_limit_reached") {
        toast.error("Достигнут лимит", {
          description:
            "Максимум 5 активных заявок на пополнение. Дождитесь завершения или истечения.",
        });
      } else {
        toast.error("Не удалось создать заявку на пополнение");
      }
    }
  });

  const canCreate = amount >= AMOUNT_CONSTRAINTS.min && form.formState.isValid;
  const exactAmount = depositData?.requested_amount_cents
    ? (depositData.requested_amount_cents / 100).toString()
    : "";

  const depositContent = (
    <div className="space-y-5">
      <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
        <Check className="size-5 text-emerald-500 shrink-0" />
        <div>
          <p className="text-sm font-medium text-foreground">
            Заявка создана — {exactAmount} USD
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            К зачислению:{" "}
            {depositData?.requested_amount_cents
              ? ((depositData.requested_amount_cents / 100) * 0.95).toFixed(2)
              : "—"}{" "}
            USD
          </p>
        </div>
      </div>

      {/* Commission info */}
      <div className="rounded-lg bg-muted/30 border p-3 space-y-1.5 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Сумма к оплате:</span>
          <span className="font-medium">{exactAmount} USD</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">
            Комиссия платформы (3%):
          </span>
          <span className="font-medium text-amber-600">
            {depositData?.requested_amount_cents
              ? ((depositData.requested_amount_cents / 100) * 0.03).toFixed(2)
              : "—"}{" "}
            USD
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground inline-flex items-center gap-1">
            Комиссия платёжной системы (2%):
            <InfoTooltip tKey="deposits.info.heleketFee" />
          </span>
          <span className="font-medium text-amber-600">
            {depositData?.requested_amount_cents
              ? ((depositData.requested_amount_cents / 100) * 0.02).toFixed(2)
              : "—"}{" "}
            USD
          </span>
        </div>
        <div className="flex items-center justify-between pt-1.5 border-t">
          <span className="text-muted-foreground font-medium">
            Будет зачислено:
          </span>
          <span className="font-semibold text-emerald-600">
            {depositData?.requested_amount_cents
              ? ((depositData.requested_amount_cents / 100) * 0.95).toFixed(2)
              : "—"}{" "}
            USD
          </span>
        </div>
      </div>

      <InfoBlock
        variant="warning"
        titleKey="deposits.info.feeWarningTitle"
        bodyKey="deposits.info.feeWarningBody"
        className="mt-4"
      />

      <div className="flex flex-col items-center gap-4">
        <Button
          onClick={handleOpenPayment}
          size="lg"
          className="w-full h-12 text-base"
        >
          <ExternalLink className="size-4 mr-2" />
          Перейти к оплате
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          Откроется страница платежного шлюза Heleket
        </p>
      </div>

      <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3">
        {depositData?.expires_at && (
          <div className="mb-2">
            <DepositCountdown expiresAt={depositData.expires_at} />
          </div>
        )}
        <p className="text-xs text-amber-600 dark:text-amber-400">
          <span className="font-semibold">Важно:</span> Завершите оплату до
          истечения времени. Средства зачислятся автоматически.
        </p>
      </div>
    </div>
  );

  const depositFooter = (
    <div className="flex gap-2">
      <Button
        variant="ghost"
        onClick={handleClose}
        className="flex-1 h-10 text-sm"
      >
        Закрыть
      </Button>
      <Button asChild className="flex-1 h-10 text-sm">
        <Link to={ROUTES.DEPOSITS} onClick={handleClose}>
          Мои транзакции
        </Link>
      </Button>
    </div>
  );

  const amountContent = (
    <Form {...form}>
      <div className="space-y-5">
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="inline-flex items-center gap-1.5">
                Сумма пополнения
                <InfoTooltip tKey="deposits.info.amountToCredit" />
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={field.value || ""}
                    onChange={handleInputChange}
                    placeholder="0"
                    className="h-12 text-lg font-semibold font-mono pr-16"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm font-medium">
                    USD
                  </span>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <span className="text-xs font-medium text-muted-foreground block">
            Быстрый выбор
          </span>
          <div className="grid grid-cols-3 gap-2">
            {QUICK_AMOUNTS.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() =>
                  form.setValue("amount", value, { shouldValidate: true })
                }
                className={
                  amount === value
                    ? "py-2 px-3 rounded-lg text-sm font-medium bg-primary text-primary-foreground border border-primary"
                    : "py-2 px-3 rounded-lg text-sm font-medium bg-muted/30 text-muted-foreground hover:bg-muted/60 hover:text-foreground border border-transparent"
                }
              >
                <Price cents={value * 100} />
              </button>
            ))}
          </div>
        </div>

        {/* Commission info */}
        <div className="rounded-lg bg-muted/30 border p-3 space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Информация о комиссиях:
          </p>
          <div className="space-y-1 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Комиссия платформы:</span>
              <span className="font-medium">3%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground inline-flex items-center gap-1">
                Комиссия платёжной системы:
                <InfoTooltip tKey="deposits.info.heleketFee" />
              </span>
              <span className="font-medium">2%</span>
            </div>
            <div className="flex items-center justify-between pt-1 border-t">
              <span className="text-muted-foreground">К зачислению:</span>
              <span className="font-medium text-emerald-600">
                {amount > 0 ? `${(amount * 0.95).toFixed(2)} USD` : "—"}
              </span>
            </div>
          </div>
        </div>

        <InfoBlock
          variant="info"
          titleKey="deposits.info.howTitle"
          bodyKey="deposits.info.howBody"
          itemsKey="deposits.info.howSteps"
          className="mt-4"
        />
      </div>
    </Form>
  );

  const amountFooter = (
    <div className="flex gap-2">
      <Button
        type="button"
        variant="ghost"
        onClick={handleClose}
        disabled={createDeposit.isPending}
        className="flex-1 h-10"
      >
        Отмена
      </Button>
      <Button
        type="button"
        onClick={handleCreate}
        disabled={!canCreate || createDeposit.isPending}
        className="flex-1 h-10"
      >
        {createDeposit.isPending ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="size-4 animate-spin" />
            Создание...
          </span>
        ) : (
          "Создать заявку"
        )}
      </Button>
    </div>
  );

  return (
    <ResponsiveModal open={isOpen} onOpenChange={handleClose} modal={modal}>
      <ResponsiveModal.Content className="max-w-sm">
        <ResponsiveModal.Header>
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
              <Wallet className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col gap-1">
              <ResponsiveModal.Title>Пополнение баланса</ResponsiveModal.Title>
              <ResponsiveModal.Description>
                Deposit funds to your account
              </ResponsiveModal.Description>
            </div>
          </div>
        </ResponsiveModal.Header>

        <ResponsiveModal.Body>
          {depositData ? (
            <div className="space-y-4">{depositContent}</div>
          ) : (
            amountContent
          )}
        </ResponsiveModal.Body>

        <ResponsiveModal.Footer>
          {depositData ? depositFooter : amountFooter}
        </ResponsiveModal.Footer>
      </ResponsiveModal.Content>
    </ResponsiveModal>
  );
}
